import React from 'react';
import { X, Loader2, AlertCircle, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import type { AttachmentItem } from '../../../application/services/notesToHank/NotesToHankTypes';

export interface V2AttachmentPreviewProps {
  attachments: AttachmentItem[];
  onRemove: (index: number) => void;
}

function truncateFileName(name: string, maxLength = 20): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength) + '…';
}

export const V2AttachmentPreview: React.FC<V2AttachmentPreviewProps> = ({
  attachments,
  onRemove,
}) => {
  if (attachments.length === 0) return null;

  return (
    <div className="flex gap-2 px-3 py-2 border-t border-border overflow-x-auto">
      {attachments.map((attachment, index) => (
        <div key={`${attachment.uri}-${index}`} className="relative shrink-0">
          {attachment.type === 'photo' ? (
            <img
              src={attachment.uri}
              alt={attachment.fileName || 'Attachment'}
              className="w-16 h-16 rounded-md object-cover"
            />
          ) : (
            <div className="flex items-center gap-1.5 h-16 px-3 rounded-md bg-muted text-foreground text-xs">
              <FileText className="size-4 shrink-0" />
              <span>{truncateFileName(attachment.fileName || 'file')}</span>
            </div>
          )}

          {/* Upload status overlays */}
          {attachment.uploadStatus === 'uploading' && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
              <Loader2 className="size-5 text-white animate-spin" />
            </div>
          )}
          {attachment.uploadStatus === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
              <AlertCircle className="size-5 text-destructive" />
            </div>
          )}

          {/* Remove button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-foreground/80 text-background hover:bg-foreground"
            onClick={() => onRemove(index)}
            aria-label="Remove attachment"
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export default V2AttachmentPreview;
